import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Image, Upload, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getImageSrc(url) {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newImage, setNewImage] = useState({ url: '', caption: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data } = await axios.get(`${API}/gallery`, { withCredentials: true });
      setImages(data);
    } catch (error) {
      toast.error('Failed to fetch gallery');
    } finally {
      setLoading(false);
    }
  };

  const addImageByUrl = async (e) => {
    e.preventDefault();
    if (!newImage.url) {
      toast.error('Please enter an image URL');
      return;
    }

    try {
      await axios.post(`${API}/gallery`, newImage, { withCredentials: true });
      toast.success('Image added to gallery');
      fetchGallery();
      setIsDialogOpen(false);
      setNewImage({ url: '', caption: '' });
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WEBP images are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/gallery/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded to gallery!');
      fetchGallery();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { withCredentials: true });
      toast.success('Image removed');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="gallery-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Gallery
          </h1>
          <p className="text-[#756A63] mt-1">Manage your portfolio images for the carousel</p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white"
          data-testid="add-image-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Image
        </Button>
      </div>

      {images.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E3DACD] p-8 text-center">
          <Image className="w-12 h-12 mx-auto mb-4 text-[#E3DACD]" />
          <p className="text-[#756A63]">No images yet. Add your first gallery image!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image) => (
            <div
              key={image.id}
              className="bg-white rounded-xl border border-[#E3DACD] overflow-hidden group relative"
              data-testid={`gallery-item-${image.id}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={getImageSrc(image.url)}
                  alt={image.caption || 'Gallery image'}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x500?text=Image+Not+Found';
                  }}
                />
              </div>
              <div className="p-4">
                <p className="text-sm text-[#756A63] truncate">
                  {image.caption || 'No caption'}
                </p>
              </div>
              <Button
                size="sm"
                variant="destructive"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => deleteImage(image.id)}
                data-testid={`delete-image-btn-${image.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add Image Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Gallery Image</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload" data-testid="upload-tab">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="url" data-testid="url-tab">
                <LinkIcon className="w-4 h-4 mr-2" />
                URL
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="upload" className="space-y-4 mt-4">
              <div
                className="border-2 border-dashed border-[#E3DACD] rounded-xl p-8 text-center cursor-pointer hover:border-[#9C6A48] transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-10 h-10 mx-auto mb-3 text-[#9C6A48]" />
                <p className="text-sm font-medium text-[#2B2623]">
                  {uploading ? 'Uploading...' : 'Click to upload an image'}
                </p>
                <p className="text-xs text-[#756A63] mt-1">JPEG, PNG, GIF, WEBP up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="file-upload-input"
              />
              {uploading && (
                <div className="flex items-center justify-center gap-2 text-sm text-[#9C6A48]">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#9C6A48]"></div>
                  Uploading...
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="url" className="mt-4">
              <form onSubmit={addImageByUrl} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#2B2623] mb-2 block">Image URL *</label>
                  <Input
                    data-testid="image-url-input"
                    value={newImage.url}
                    onChange={(e) => setNewImage({ ...newImage, url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    required
                    className="border-[#E3DACD]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2623] mb-2 block">Caption (optional)</label>
                  <Input
                    data-testid="image-caption-input"
                    value={newImage.caption}
                    onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
                    placeholder="Describe this image..."
                    className="border-[#E3DACD]"
                  />
                </div>
                {newImage.url && (
                  <div className="rounded-xl overflow-hidden border border-[#E3DACD]">
                    <img
                      src={newImage.url}
                      alt="Preview"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+URL';
                      }}
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="border-[#E3DACD]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white"
                    data-testid="save-image-btn"
                  >
                    Add Image
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
